import { type DateValue, type Time } from '@internationalized/date';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { type DiscountsTableRow, DiscountStatus } from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Box,
	Button,
	Drawer,
	Group,
	SkeletonRect,
	Stack,
	Tag,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import { ChevronDown12, ChevronDown24, ChevronUp24, SquareUp24 } from '@common/components/icons';
import Bin24 from '@common/components/icons/Bin24';
import CheckCircle24 from '@common/components/icons/CheckCircle24';
import CircleSlash24 from '@common/components/icons/CircleSlash24';
import Duplicate24 from '@common/components/icons/Duplicate24';

import { useCreateDiscount } from '../api/useCreateDiscount';
import { useExportDiscounts } from '../api/useExportDiscounts';
import { useGetDiscount } from '../api/useGetDiscount';
import { useSearchDiscounts } from '../api/useSearchDiscounts';
import { useUpdateDiscount } from '../api/useUpdateDiscount';
import {
	discountDetailsDrawerAtom,
	DiscountDetailsTab,
	discountDrawerModeAtom,
	discountFormDirtyAtom,
	NEW_DISCOUNT_ID,
} from '../atoms/discountDetailsDrawerAtoms';
import {
	DISCOUNT_FORM_DEFAULTS,
	discountFormAtom,
	discountFormErrorsAtom,
	validateDiscountForm,
} from '../atoms/discountFormAtoms';
import { discountsParamsAtom } from '../atoms/discountsAtoms';
import { useDiscountNavigation } from '../hooks/useDiscountNavigation';
import { mapResponseToFormState } from '../utils/mapResponseToFormState';

import {
	DiscountActiveDatesSection,
	DiscountTypeSection,
	DiscountValueSection,
} from './drawer-sections';
import { DISCOUNT_STATUS_TAG_CONFIG } from './types';

import '@common/components/_drawerShared.scss';
/** Converts DateValue + TimeValue back to Unix ms timestamp. */
const buildTimestamp = (date: DateValue | null, time: Time | null): number | undefined => {
	if (!date) return undefined;
	const jsDate = new Date(
		date.year,
		date.month - 1,
		date.day,
		time?.hour ?? 0,
		time?.minute ?? 0,
	);
	return jsDate.getTime();
};

const DRAWER_TITLES: Record<string, string> = {
	create: 'Create discount',
	duplicate: 'Duplicate discount',
	view: 'View discount',
};

export const DiscountDetailsDrawer = () => {
	// Get URL params
	const { discountId, detailsTab } = useParams<{
		discountId?: string;
		detailsTab?: string;
	}>();
	const {
		closeDiscountDetails,
		openDiscountDetails,
		navigateToPreviousDiscount,
		navigateToNextDiscount,
		getNavigationState,
	} = useDiscountNavigation();

	// Determine if we are in create mode based on URL sentinel
	const isCreateMode = discountId === NEW_DISCOUNT_ID;

	// Drawer is open if discountId and detailsTab are present in URL
	const isDrawerOpen = Boolean(discountId && detailsTab);
	const activeTab = (detailsTab as DiscountDetailsTab) || DiscountDetailsTab.OVERVIEW;

	// Get discounts data to find the selected discount (skip lookup in create mode)
	const params = useAtomValue(discountsParamsAtom);
	const { data } = useSearchDiscounts(params);
	const selectedRow = isCreateMode
		? null
		: data?.data.find((discount: DiscountsTableRow) => discount.id === discountId) || null;
	const totalCount = data?.count ?? 0;

	// Fetch full discount campaign data for the drawer form (skip in create mode)
	const { data: discountCampaign, isLoading: isLoadingDiscount } = useGetDiscount(
		isDrawerOpen && !isCreateMode ? discountId : undefined,
	);

	// Drawer mode and form state
	const [drawerMode, setDrawerMode] = useAtom(discountDrawerModeAtom);
	const [formState, setFormState] = useAtom(discountFormAtom);
	const isFormDirty = useAtomValue(discountFormDirtyAtom);
	const setFormDirty = useSetAtom(discountFormDirtyAtom);

	// Validation errors
	const setFormErrors = useSetAtom(discountFormErrorsAtom);

	// Mutations
	const createMutation = useCreateDiscount();
	const updateMutation = useUpdateDiscount();
	const exportMutation = useExportDiscounts();

	// Track the discount ID that was last populated to avoid re-populating on re-renders
	const lastPopulatedIdRef = useRef<string | null>(null);

	// Sync drawer mode with URL: 'new' sentinel means create mode, otherwise view
	useEffect(() => {
		if (isCreateMode) {
			// Preserve 'duplicate' mode when navigating to the new discount sentinel
			if (drawerMode !== 'duplicate') {
				setDrawerMode('create');
			}
		} else if (discountId && (drawerMode === 'create' || drawerMode === 'duplicate')) {
			setDrawerMode('view');
		}
	}, [discountId, isCreateMode, setDrawerMode, drawerMode]);

	// Get navigation state for prev/next buttons (not applicable in create mode)
	const navigationState =
		discountId && !isCreateMode
			? getNavigationState(discountId, totalCount)
			: { canGoPrevious: false, canGoNext: false };

	// Sync selectedRow to atom for child components
	const setDrawerState = useSetAtom(discountDetailsDrawerAtom);
	useEffect(() => {
		setDrawerState({ selectedRow });
	}, [selectedRow, setDrawerState]);

	// Populate form when discount campaign data loads (view mode)
	useEffect(() => {
		if (discountCampaign && discountId && lastPopulatedIdRef.current !== discountId) {
			const mappedState = mapResponseToFormState(discountCampaign);
			setFormState(mappedState);
			setFormDirty(false);
			lastPopulatedIdRef.current = discountId;
		}
	}, [discountCampaign, discountId, setFormState, setFormDirty]);

	// Reset form and tracking when drawer closes
	useEffect(() => {
		if (!isDrawerOpen) {
			lastPopulatedIdRef.current = null;
			setFormState(DISCOUNT_FORM_DEFAULTS);
			setFormDirty(false);
			setFormErrors({});
			setDrawerMode('view');
		}
	}, [isDrawerOpen, setFormState, setFormDirty, setFormErrors, setDrawerMode]);

	// Close drawer handler
	const closeDrawer = useCallback(() => {
		closeDiscountDetails();
	}, [closeDiscountDetails]);

	// Action handlers
	const handleDuplicate = () => {
		if (!discountCampaign) return;
		const formData = mapResponseToFormState(discountCampaign);
		// Preserve all fields except code/title so the user provides new ones
		setFormState({ ...formData, code: '', title: '' });
		setFormDirty(false);
		setDrawerMode('duplicate');
		openDiscountDetails(NEW_DISCOUNT_ID, DiscountDetailsTab.OVERVIEW);
	};

	const handleExport = async (): Promise<void> => {
		if (!discountId) return;
		try {
			await exportMutation.mutateAsync({
				discountIds: [discountId],
			});
			toast({
				message: '1 discount exported',
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to export discount. Please try again.',
				variant: 'error',
			});
		}
	};

	const handleToggleActive = () => {
		toast({ message: 'Toggle discount status — coming soon', variant: 'info' });
	};

	const handleDelete = () => {
		toast({ message: 'Delete discount — coming soon', variant: 'info' });
	};

	// Cancel: revert form to the fetched discount data
	const handleCancel = useCallback(() => {
		if (discountCampaign) {
			const mappedState = mapResponseToFormState(discountCampaign);
			setFormState(mappedState);
		}
		setFormDirty(false);
		if (isCreateMode || drawerMode === 'duplicate') {
			closeDrawer();
		}
	}, [discountCampaign, drawerMode, isCreateMode, closeDrawer, setFormState, setFormDirty]);

	// Save: create or update depending on mode
	const handleSave = useCallback(() => {
		const errors = validateDiscountForm(formState);
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
			return;
		}
		setFormErrors({});

		if (isCreateMode) {
			const createPayload = {
				name: formState.title,
				discountType: formState.discountType,
				amountType: formState.amountType,
				method: formState.method,
				code: formState.code || undefined,
				amount: Number(formState.amount),
				appliesTo: formState.appliesTo,
				applicableProducts: formState.applicableProducts,
				startsAt: buildTimestamp(formState.startDate, formState.startTime) ?? Date.now(),
				hasEndDate: formState.hasEndDate,
				endsAt: formState.hasEndDate
					? buildTimestamp(formState.endDate, formState.endTime)
					: undefined,
			};
			createMutation.mutate(createPayload, {
				onSuccess: () => {
					setFormDirty(false);
					closeDrawer();
				},
			});
		} else {
			if (!discountId) return;
			const updatePayload = {
				name: formState.title,
				discountType: formState.discountType,
				method: formState.method,
				code: formState.code,
				amountType: formState.amountType,
				amount: Number(formState.amount),
				appliesTo: formState.appliesTo,
				applicableProducts: formState.applicableProducts,
				startsAt: buildTimestamp(formState.startDate, formState.startTime),
				hasEndDate: formState.hasEndDate,
				endsAt: formState.hasEndDate
					? buildTimestamp(formState.endDate, formState.endTime)
					: undefined,
			};
			updateMutation.mutate(
				{ discountId, payload: updatePayload },
				{
					onSuccess: () => {
						toast({ message: 'Discount saved successfully', variant: 'success' });
						setFormDirty(false);
						setDrawerMode('view');
						// Reset the populated ref so the form re-populates with fresh data
						lastPopulatedIdRef.current = null;
					},
					onError: () => {
						toast({ message: 'Failed to save discount', variant: 'error' });
					},
				},
			);
		}
	}, [
		discountId,
		formState,
		isCreateMode,
		createMutation,
		updateMutation,
		setFormDirty,
		setFormErrors,
		setDrawerMode,
		closeDrawer,
	]);

	const handlePreviousDiscount = () => {
		if (discountId && navigationState.canGoPrevious) {
			// Reset populated ref so next discount's data populates
			lastPopulatedIdRef.current = null;
			navigateToPreviousDiscount(discountId, activeTab);
		}
	};

	const handleNextDiscount = () => {
		if (discountId && navigationState.canGoNext) {
			// Reset populated ref so next discount's data populates
			lastPopulatedIdRef.current = null;
			navigateToNextDiscount(discountId, activeTab);
		}
	};

	// Don't render if no discount is selected and not in create mode
	if (!selectedRow && !isCreateMode) {
		return null;
	}

	const isExpired = !isCreateMode && selectedRow?.status.value === DiscountStatus.EXPIRED;

	const statusTagConfig = selectedRow?.status.value
		? DISCOUNT_STATUS_TAG_CONFIG[selectedRow.status.value]
		: undefined;

	const drawerTitle = (
		<Group itemsSpacing='8' noWrap itemsAlignY='center'>
			<Text size='heading' weight='semibold'>
				{drawerMode === 'create' || drawerMode === 'duplicate'
					? DRAWER_TITLES[drawerMode]
					: (selectedRow?.header.name ?? DRAWER_TITLES[drawerMode] ?? 'View discount')}
			</Text>
			{!isCreateMode && statusTagConfig && (
				<Tag accent={statusTagConfig.accent} size='medium'>
					{statusTagConfig.label}
				</Tag>
			)}
		</Group>
	);

	return (
		<DialogTrigger
			isOpen={isDrawerOpen}
			onOpenChange={(open) => {
				if (!open) {
					closeDrawer();
				}
			}}>
			<Drawer
				placement='right'
				hasCloseButton={true}
				isDismissable={true}
				isKeyboardDismissDisabled={false}
				ariaLabel='Discount Details'
				className='drawer--custom'>
				<Drawer.Header>{drawerTitle}</Drawer.Header>
				<Drawer.Content padding={{ x: 24, top: 12, bottom: 64 }}>
					<Stack itemsSpacing='16'>
						{!isCreateMode && isLoadingDiscount ? (
							<>
								<SkeletonRect width='100%' height={180} rounding='12' />
								<SkeletonRect width='100%' height={140} rounding='12' />
								<SkeletonRect width='100%' height={160} rounding='12' />
							</>
						) : (
							<>
								<Box elevation='0' rounding='12' padding='16'>
									<DiscountTypeSection />
								</Box>
								<Box elevation='0' rounding='12' padding='16'>
									<DiscountValueSection />
								</Box>
								<Box elevation='0' rounding='12' padding='16'>
									<DiscountActiveDatesSection />
								</Box>
							</>
						)}
					</Stack>
				</Drawer.Content>
				{/* Footer with actions and navigation */}
				<Drawer.Footer>
					<Group itemsSpacing='8' itemsAlignX='space-between' noWrap>
						{!isCreateMode && (
							<Group.Item>
								<ActionMenuTrigger>
									<Button
										variant='secondary'
										size='large'
										icon={<ChevronDown12 />}
										iconSide='right'>
										More actions
									</Button>
									<ActionMenu>
										<ActionMenuItem
											label='Duplicate'
											leadingAccessory={<Duplicate24 />}
											onAction={handleDuplicate}
										/>
										<ActionMenuItem
											label='Export'
											leadingAccessory={<SquareUp24 />}
											onAction={handleExport}
										/>
										{isExpired ? (
											<ActionMenuItem
												label='Activate'
												leadingAccessory={<CheckCircle24 />}
												onAction={handleToggleActive}
											/>
										) : (
											<ActionMenuItem
												label='Deactivate'
												leadingAccessory={<CircleSlash24 />}
												onAction={handleToggleActive}
											/>
										)}
										<ActionMenuItem
											label={
												<Text size='bodySmall' color='error'>
													Delete
												</Text>
											}
											leadingAccessory={<Bin24 />}
											onAction={handleDelete}
										/>
									</ActionMenu>
								</ActionMenuTrigger>
							</Group.Item>
						)}
						<div style={{ marginLeft: 'auto' }}>
							<Group itemsSpacing='8' noWrap>
								{(isCreateMode || isFormDirty) && (
									<>
										<Button
											variant='secondary'
											size='large'
											onPress={handleCancel}>
											Cancel
										</Button>
										<Button
											size='large'
											onPress={handleSave}
											isLoading={
												createMutation.isPending || updateMutation.isPending
											}>
											{isCreateMode ? 'Create' : 'Save'}
										</Button>
									</>
								)}
								{!isCreateMode && !isFormDirty && (
									<>
										<Button
											variant='secondary'
											size='icon'
											icon={<ChevronUp24 />}
											aria-label='Previous discount'
											onPress={handlePreviousDiscount}
											isDisabled={!navigationState.canGoPrevious}
										/>
										<Button
											variant='secondary'
											size='icon'
											icon={<ChevronDown24 />}
											aria-label='Next discount'
											onPress={handleNextDiscount}
											isDisabled={!navigationState.canGoNext}
										/>
									</>
								)}
							</Group>
						</div>
					</Group>
				</Drawer.Footer>
			</Drawer>
		</DialogTrigger>
	);
};
